/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import dayjs from "dayjs";
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import { Skeleton } from "@mui/material";
import {
  GuestActivityLogCard,
  GuestActivityLogDate,
  GuestActivityLogEmptyState,
  GuestActivityLogEvent,
  GuestActivityLogEventContent,
  GuestActivityLogEventHeader,
  GuestActivityLogEventIcon,
  GuestActivityLogEventSubtitle,
  GuestActivityLogEventTimestamp,
  GuestActivityLogEventTitle,
  GuestActivityLogGroup,
  GuestActivityLogList,
  GuestActivityLogLoadingWrap,
  GuestActivityLogTimeline,
} from "./styles";

const EVENT_GROUP_ICONS = {
  orders: ReceiptLongOutlinedIcon,
  discount_cards: CreditCardOutlinedIcon,
  guest_profile: PersonOutlineOutlinedIcon,
  mobile_activity: SmartphoneOutlinedIcon,
  experiences: WorkOutlineOutlinedIcon,
};

const formatGroupDate = (date) => {
  const parsedDate = dayjs(date);
  return parsedDate.isValid() ? parsedDate.format('MMMM YYYY') : date || '-';
};

const formatHappenedAt = (date) => {
  const parsedDate = dayjs(date);
  return parsedDate.isValid() ? parsedDate.format('MMM D, YYYY') : '-';
};

const LoadingState = () => (
  <GuestActivityLogLoadingWrap data-testid="guest-activity-log-loader">
    <Skeleton variant="text" width="35%" height={24} />
    <Skeleton variant="rectangular" width="100%" height={76} sx={{ borderRadius: '14px' }} />
    <Skeleton variant="rectangular" width="100%" height={76} sx={{ borderRadius: '14px' }} />
    <Skeleton variant="text" width="30%" height={24} />
    <Skeleton variant="rectangular" width="100%" height={76} sx={{ borderRadius: '14px' }} />
  </GuestActivityLogLoadingWrap>
);

export const GuestActivityLog = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <GuestActivityLogCard data-testid="guest-activity-log-card">
        <LoadingState />
      </GuestActivityLogCard>
    );
  }

  const groups = data || [];

  return (
    <GuestActivityLogCard data-testid="guest-activity-log-card">
      {groups.length ? (
        <GuestActivityLogTimeline data-testid="guest-activity-log-timeline">
          {groups.map((group, groupIndex) => (
            <GuestActivityLogGroup
              key={group?.date || `activity-log-group-${groupIndex}`}
              data-testid={`guest-activity-log-group-${groupIndex}`}
            >
              <GuestActivityLogDate data-testid="guest-activity-log-group-date">
                {formatGroupDate(group?.date)}
              </GuestActivityLogDate>

              <GuestActivityLogList>
                {(group?.activity_logs || []).map((activity, activityIndex) => {
                  const IconComponent = EVENT_GROUP_ICONS[activity?.event_group] || NotesOutlinedIcon;
                  const subtitle = activity?.subtitle || '-';
                  const happenedAt = activity?.happened_at;

                  return (
                    <GuestActivityLogEvent
                      key={activity?.id || `${groupIndex}-${activityIndex}`}
                      data-testid={`guest-activity-log-item-${groupIndex}-${activityIndex}`}
                    >
                      <GuestActivityLogEventIcon data-testid="guest-activity-log-item-icon">
                        <IconComponent sx={{ fontSize: '14px' }} />
                      </GuestActivityLogEventIcon>

                      <GuestActivityLogEventContent>
                        <GuestActivityLogEventHeader>
                          <GuestActivityLogEventTitle data-testid="guest-activity-log-item-title">
                            {activity?.title || '-'}
                          </GuestActivityLogEventTitle>
                        </GuestActivityLogEventHeader>

                        <GuestActivityLogEventSubtitle data-testid="guest-activity-log-item-subtitle">
                          {subtitle}
                        </GuestActivityLogEventSubtitle>

                        <GuestActivityLogEventTimestamp data-testid="guest-activity-log-item-date">
                          {formatHappenedAt(happenedAt)}
                        </GuestActivityLogEventTimestamp>
                      </GuestActivityLogEventContent>
                    </GuestActivityLogEvent>
                  );
                })}
              </GuestActivityLogList>
            </GuestActivityLogGroup>
          ))}
        </GuestActivityLogTimeline>
      ) : (
        <GuestActivityLogEmptyState data-testid="guest-activity-log-empty-state">
          No activity log entries found.
        </GuestActivityLogEmptyState>
      )}
    </GuestActivityLogCard>
  );
};
